// src/components/admin/PaymentsManagement.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { adminPaymentService, adminBookingService } from '../../services/adminService';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt      = (n) => Number(n || 0).toLocaleString('en-IN');
const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('en-IN',  { day:'2-digit', month:'short', year:'numeric' }) : '—';
const fmtDT    = (d) => d ? new Date(d).toLocaleString('en-IN') : '—';

const STATUS_STYLE = {
  initiated: { bg:'#fff8e1', color:'#f59e0b' },
  pending:   { bg:'#fff8e1', color:'#f59e0b' },
  success:   { bg:'#e6f9f0', color:'#16a34a' },
  confirmed: { bg:'#e0f2fe', color:'#0284c7' },
  completed: { bg:'#e6f9f0', color:'#16a34a' },
  failed:    { bg:'#fef2f2', color:'#dc2626'  },
  cancelled: { bg:'#f3f4f6', color:'#6b7280'  },
  refunded:  { bg:'#f5f3ff', color:'#7c3aed'  },
};

const Badge = ({ status }) => {
  const s = STATUS_STYLE[status] || { bg:'#f3f4f6', color:'#6b7280' };
  return (
    <span style={{ background:s.bg, color:s.color, padding:'3px 10px', borderRadius:20, fontWeight:600, fontSize:12, textTransform:'capitalize', whiteSpace:'nowrap' }}>
      {status || '—'}
    </span>
  );
};

const TABS = [
  { id:'payments', label:'💳  Payments' },
  { id:'bookings', label:'📋  Bookings' },
];

// ════════════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════════
const PaymentsManagement = () => {
  const [activeTab,       setActiveTab]       = useState('payments');
  const [payments,        setPayments]        = useState([]);
  const [bookings,        setBookings]        = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [filter,          setFilter]          = useState('all');
  const [searchTerm,      setSearchTerm]      = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // ── Fetch data ──────────────────────────────────────────────────────────────
  const loadPayments = useCallback(async () => {
    try {
      const res = await adminPaymentService.getAllPayments();
      setPayments(Array.isArray(res) ? res : []);
    } catch (e) {
      console.error('Payments error:', e);
    }
  }, []);

  const loadBookings = useCallback(async () => {
    try {
      const res = await adminBookingService.getAllBookings();
      // getAllBookings already unwraps { bookings: [] } → plain array
      setBookings(Array.isArray(res) ? res : []);
      setError(null);
    } catch (e) {
      console.error('Bookings error:', e);
      // Show a helpful message if the admin/bookings route doesn't exist yet
      if (e?.response?.status === 404 || e?.response?.status === 401) {
        setError(`Could not load bookings: ${e?.response?.data?.error || 'Route not found. Please add /admin/bookings to your Rails routes.'}`);
      } else {
        setError('Failed to load bookings. Check console for details.');
      }
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadPayments(), loadBookings()]);
      setLoading(false);
    };
    init();
  }, [loadPayments, loadBookings]);

  // ── Actions ─────────────────────────────────────────────────────────────────
  const handleRefund = async (paymentId) => {
    if (!window.confirm('Refund this payment?')) return;
    try {
      await adminPaymentService.refundPayment(paymentId);
      alert('Refunded successfully!');
      loadPayments();
    } catch (e) {
      alert('Refund failed: ' + (e?.response?.data?.error || e.message));
    }
  };

  const handleCancelBooking = async (bookingId) => {
    const reason = window.prompt('Cancellation reason (optional):');
    if (reason === null) return;
    try {
      await adminBookingService.cancelBooking(bookingId, reason || null);
      alert('Booking cancelled!');
      setSelectedBooking(null);
      loadBookings();
    } catch (e) {
      alert('Cancel failed: ' + (e?.response?.data?.error || e.message));
    }
  };

  const handleConfirmBooking = async (bookingId) => {
    if (!window.confirm('Confirm this booking?')) return;
    try {
      await adminBookingService.confirmBooking(bookingId);
      alert('Booking confirmed!');
      loadBookings();
    } catch (e) {
      alert('Confirm failed: ' + (e?.response?.data?.error || e.message));
    }
  };

  // ── Stats ───────────────────────────────────────────────────────────────────
  const payStats = {
    revenue:  payments.filter(p => p.status === 'success').reduce((s, p) => s + (p.amount_cents / 100), 0),
    pending:  payments.filter(p => p.status === 'initiated').length,
    refunded: payments.filter(p => p.status === 'refunded').length,
    failed:   payments.filter(p => p.status === 'failed').length,
  };

  const bookStats = {
    total:     bookings.length,
    pending:   bookings.filter(b => b.status === 'pending').length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length,
    revenue:   bookings
      .filter(b => ['confirmed','completed'].includes(b.status))
      .reduce((s, b) => s + (b.amount_cents / 100), 0),
  };

  // ── Filtered lists ──────────────────────────────────────────────────────────
  const q = searchTerm.toLowerCase().trim();

  const filteredPayments = payments.filter(p => {
    const okFilter = filter === 'all' || p.status === filter;
    const okSearch = !q ||
      String(p.id).includes(q) ||
      p.razorpay_order_id?.toLowerCase().includes(q) ||
      p.razorpay_payment_id?.toLowerCase().includes(q) ||
      String(p.user_id).includes(q);
    return okFilter && okSearch;
  });

  const filteredBookings = bookings.filter(b => {
    const okFilter = filter === 'all' || b.status === filter;
    const okSearch = !q ||
      String(b.id).includes(q) ||
      String(b.user_id).includes(q) ||
      String(b.car_id).includes(q) ||
      b.user?.name?.toLowerCase().includes(q) ||
      b.user?.email?.toLowerCase().includes(q) ||
      b.user?.phone?.toLowerCase().includes(q) ||
      b.car?.title?.toLowerCase().includes(q) ||
      b.car?.brand?.toLowerCase().includes(q) ||
      b.car?.model?.toLowerCase().includes(q);
    return okFilter && okSearch;
  });

  // ── Loading screen ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:320, gap:16 }}>
        <div style={{ width:44, height:44, border:'4px solid #e5e7eb', borderTop:'4px solid #f59e0b', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
        <p style={{ color:'#9ca3af', margin:0 }}>Loading payments & bookings…</p>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding:24, fontFamily:'Inter,sans-serif', maxWidth:'100%' }}>

      {/* Header */}
      <div style={{ marginBottom:20 }}>
        <h1 style={{ margin:0, fontSize:24, fontWeight:700 }}>Payments &amp; Bookings</h1>
        <p style={{ margin:'4px 0 0', color:'#6b7280', fontSize:14 }}>Manage all transactions and car bookings</p>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', borderBottom:'2px solid #e5e7eb', marginBottom:24, gap:4 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => { setActiveTab(t.id); setFilter('all'); setSearchTerm(''); }}
            style={{
              padding:'10px 22px', border:'none', cursor:'pointer', fontSize:14, fontWeight:600, background:'none',
              borderBottom: activeTab === t.id ? '3px solid #f59e0b' : '3px solid transparent',
              color: activeTab === t.id ? '#f59e0b' : '#6b7280',
              marginBottom:-2, transition:'color .2s',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ════ PAYMENTS TAB ════ */}
      {activeTab === 'payments' && (
        <>
          <StatsRow>
            <StatCard icon="💰" label="Total Revenue"  value={`₹${fmt(payStats.revenue)}`} sub="Successful payments" color="#16a34a" />
            <StatCard icon="⏳" label="Pending"         value={payStats.pending}              sub="Initiated"          color="#f59e0b" />
            <StatCard icon="↩️" label="Refunded"        value={payStats.refunded}             sub="Refunded"           color="#7c3aed" />
            <StatCard icon="❌" label="Failed"          value={payStats.failed}               sub="Failed"             color="#dc2626" />
          </StatsRow>

          <Filters
            searchTerm={searchTerm} setSearchTerm={setSearchTerm}
            placeholder="Search by order ID, payment ID, or user ID…"
            filter={filter} setFilter={setFilter}
            filters={['all','initiated','success','failed','refunded']}
            allItems={payments}
          />

          <TableWrap>
            <table style={tbl}>
              <thead>
                <tr style={{ background:'#f9fafb' }}>
                  {['Payment ID','User ID','Type','Amount','Order ID','Status','Date','Actions'].map(h => (
                    <th key={h} style={TH}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredPayments.length === 0
                  ? <tr><td colSpan={8} style={noData}>No payments found</td></tr>
                  : filteredPayments.map(p => (
                    <tr key={p.id} style={{ borderBottom:'1px solid #f3f4f6' }}>
                      <td style={TD}><strong>#{p.id}</strong></td>
                      <td style={TD}>
                          <div>
                            <div style={{ fontWeight:600 }}>{p.user?.name}</div>
                            <div style={{ fontSize:11, color:'#9ca3af' }}>{p.user?.email}</div>
                          </div>
                        </td>
                      <td style={TD}>
                        <span style={pill}>
                          {p.payable_type === 'CarInspection' ? '🔍 Inspection' : p.payable_type === 'Booking' ? '📋 Booking' : p.payable_type || '—'}
                        </span>
                      </td>
                      <td style={TD}><strong>₹{fmt(p.amount_cents / 100)}</strong></td>
                      <td style={TD}><code style={{ fontSize:11 }}>{p.razorpay_order_id?.substring(0,20)}…</code></td>
                      <td style={TD}><Badge status={p.status} /></td>
                      <td style={TD}>{fmtDate(p.created_at)}</td>
                      <td style={TD}>
                        <Btns>
                          <Btn title="View" onClick={() => setSelectedPayment(p)}>👁️</Btn>
                          {p.status === 'success' && <Btn title="Refund" danger onClick={() => handleRefund(p.id)}>↩️</Btn>}
                        </Btns>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </TableWrap>
        </>
      )}

      {/* ════ BOOKINGS TAB ════ */}
      {activeTab === 'bookings' && (
        <>
          <StatsRow>
            <StatCard icon="📋" label="Total Bookings"  value={bookStats.total}               sub="All bookings"         color="#0284c7" />
            <StatCard icon="⏳" label="Pending"          value={bookStats.pending}              sub="Awaiting payment"     color="#f59e0b" />
            <StatCard icon="✅" label="Confirmed"        value={bookStats.confirmed}            sub="Active reservations"  color="#16a34a" />
            <StatCard icon="❌" label="Cancelled"        value={bookStats.cancelled}            sub="Cancelled"            color="#dc2626" />
            <StatCard icon="💰" label="Booking Revenue" value={`₹${fmt(bookStats.revenue)}`}  sub="Confirmed + Completed" color="#7c3aed" />
          </StatsRow>

          {/* Error banner if backend route missing */}
          {error && (
            <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8, padding:'12px 16px', marginBottom:16, color:'#dc2626', fontSize:13 }}>
              ⚠️ {error}
            </div>
          )}

          <Filters
            searchTerm={searchTerm} setSearchTerm={setSearchTerm}
            placeholder="Search by booking ID, user name/email, car name…"
            filter={filter} setFilter={setFilter}
            filters={['all','pending','confirmed','completed','cancelled']}
            allItems={bookings}
          />

          <TableWrap>
            <table style={tbl}>
              <thead>
                <tr style={{ background:'#f9fafb' }}>
                  {['Booking ID','Customer','Car','Booking Amt','Car Price','Status','Scheduled','Actions'].map(h => (
                    <th key={h} style={TH}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredBookings.length === 0
                  ? <tr><td colSpan={8} style={noData}>{error ? 'Could not load bookings' : 'No bookings found'}</td></tr>
                  : filteredBookings.map(b => {
                    const user = b.user || {};
                    const car  = b.car  || {};
                    return (
                      <tr key={b.id} style={{ borderBottom:'1px solid #f3f4f6' }}>
                        <td style={TD}><strong>#{b.id}</strong></td>

                        {/* User cell */}
                        <td style={TD}>
                          <div style={{ lineHeight:1.5 }}>
                            <div style={{ fontWeight:600, fontSize:13 }}>
                              {user.name || `User #${b.user_id}`}
                            </div>
                            {user.email && <div style={{ fontSize:11, color:'#9ca3af' }}>{user.email}</div>}
                            {(user.phone || user.mobile_number) && (
                              <div style={{ fontSize:11, color:'#9ca3af' }}>{user.phone || user.mobile_number}</div>
                            )}
                          </div>
                        </td>

                        {/* Car cell */}
                        <td style={TD}>
                          <div style={{ lineHeight:1.5 }}>
                            <div style={{ fontWeight:600, fontSize:13 }}>
                              {car.title || (car.brand ? `${car.brand} ${car.model}` : `Car #${b.car_id}`)}
                            </div>
                            {car.year && (
                              <div style={{ fontSize:11, color:'#9ca3af' }}>
                                {car.year}{car.location ? ` · ${car.location}` : ''}
                              </div>
                            )}
                          </div>
                        </td>

                        <td style={TD}><strong style={{ color:'#16a34a' }}>₹{fmt(b.amount_cents / 100)}</strong></td>
                        <td style={TD}>{car.price ? `₹${fmt(car.price)}` : '—'}</td>
                        <td style={TD}><Badge status={b.status} /></td>
                        <td style={TD}>{fmtDate(b.scheduled_at)}</td>
                        <td style={TD}>
                          <Btns>
                            <Btn title="View Details" onClick={() => setSelectedBooking(b)}>👁️</Btn>
                            {b.status === 'pending' && (
                              <Btn title="Confirm" onClick={() => handleConfirmBooking(b.id)}>✅</Btn>
                            )}
                            {['pending','confirmed'].includes(b.status) && (
                              <Btn title="Cancel" danger onClick={() => handleCancelBooking(b.id)}>🚫</Btn>
                            )}
                          </Btns>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </TableWrap>
        </>
      )}

      {/* Modals */}
      {selectedPayment && (
        <PaymentModal payment={selectedPayment} onClose={() => setSelectedPayment(null)} onRefund={handleRefund} />
      )}
      {selectedBooking && (
        <BookingModal
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
          onCancel={handleCancelBooking}
          onConfirm={handleConfirmBooking}
        />
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
//  SHARED SUB-COMPONENTS
// ════════════════════════════════════════════════════════════════════════════════

const StatsRow = ({ children }) => (
  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:14, marginBottom:22 }}>
    {children}
  </div>
);

const StatCard = ({ icon, label, value, sub, color }) => (
  <div style={{ background:'#fff', borderRadius:12, padding:'18px 20px', boxShadow:'0 1px 4px rgba(0,0,0,.07)', display:'flex', gap:14, alignItems:'center' }}>
    <div style={{ fontSize:26, width:48, height:48, borderRadius:10, background:color+'1a', display:'grid', placeItems:'center', flexShrink:0 }}>{icon}</div>
    <div>
      <p style={{ margin:0, fontSize:12, color:'#9ca3af' }}>{label}</p>
      <p style={{ margin:'3px 0 2px', fontSize:20, fontWeight:700, color }}>{value}</p>
      <p style={{ margin:0, fontSize:11, color:'#9ca3af' }}>{sub}</p>
    </div>
  </div>
);

const Filters = ({ searchTerm, setSearchTerm, placeholder, filter, setFilter, filters, allItems }) => (
  <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:18, alignItems:'center' }}>
    <div style={{ position:'relative', flex:'1 1 260px' }}>
      <svg style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', opacity:.45 }} width="17" height="17" viewBox="0 0 24 24" fill="none">
        <circle cx="11" cy="11" r="8" stroke="#374151" strokeWidth="2"/>
        <path d="M21 21L16.65 16.65" stroke="#374151" strokeWidth="2" strokeLinecap="round"/>
      </svg>
      <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder={placeholder}
        style={{ width:'100%', padding:'9px 12px 9px 36px', border:'1px solid #e5e7eb', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }}
      />
    </div>
    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
      {filters.map(f => {
        const cnt = f === 'all' ? allItems.length : allItems.filter(x => x.status === f).length;
        return (
          <button key={f} onClick={() => setFilter(f)}
            style={{
              padding:'7px 14px', border:'none', borderRadius:20, cursor:'pointer', fontSize:13, fontWeight:600,
              background: filter === f ? '#f59e0b' : '#f3f4f6',
              color:      filter === f ? '#fff'    : '#374151',
              transition:'all .2s',
            }}
          >
            {f.charAt(0).toUpperCase()+f.slice(1)}{f !== 'all' && <span style={{ marginLeft:5, opacity:.75 }}>({cnt})</span>}
          </button>
        );
      })}
    </div>
  </div>
);

const TableWrap = ({ children }) => (
  <div style={{ background:'#fff', borderRadius:12, boxShadow:'0 1px 4px rgba(0,0,0,.07)', overflow:'auto' }}>
    {children}
  </div>
);

const Btns = ({ children }) => <div style={{ display:'flex', gap:6 }}>{children}</div>;

const Btn = ({ children, onClick, title, danger }) => (
  <button title={title} onClick={onClick}
    style={{ padding:'6px 9px', border:'none', borderRadius:6, cursor:'pointer', fontSize:15, background: danger ? '#fef2f2' : '#f3f4f6', transition:'background .15s' }}
    onMouseOver={e => e.currentTarget.style.background = danger ? '#fee2e2' : '#e5e7eb'}
    onMouseOut={e => e.currentTarget.style.background  = danger ? '#fef2f2' : '#f3f4f6'}
  >
    {children}
  </button>
);

// ── Modal shell ───────────────────────────────────────────────────────────────
const Modal = ({ onClose, title, children }) => (
  <div onClick={onClose}
    style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
  >
    <div onClick={e => e.stopPropagation()}
      style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:660, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,.2)' }}
    >
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'18px 24px', borderBottom:'1px solid #f3f4f6' }}>
        <h2 style={{ margin:0, fontSize:17, fontWeight:700 }}>{title}</h2>
        <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#9ca3af' }}>✕</button>
      </div>
      <div style={{ padding:24 }}>{children}</div>
    </div>
  </div>
);

// ── Detail row ────────────────────────────────────────────────────────────────
const DR = ({ label, value }) => (
  <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
    <span style={{ fontSize:11, color:'#9ca3af', textTransform:'uppercase', letterSpacing:.4 }}>{label}</span>
    <span style={{ fontSize:14, fontWeight:500, color:'#111827' }}>{value || '—'}</span>
  </div>
);

const Sec = ({ children }) => (
  <h3 style={{ margin:'18px 0 10px', fontSize:13, fontWeight:700, color:'#374151', textTransform:'uppercase', letterSpacing:.5 }}>{children}</h3>
);

const Grid2 = ({ children }) => (
  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))', gap:'10px 18px' }}>{children}</div>
);

// ── Payment Modal ─────────────────────────────────────────────────────────────
const PaymentModal = ({ payment, onClose, onRefund }) => (
  <Modal onClose={onClose} title={`Payment #${payment.id}`}>

    <Sec>👤 User Details</Sec>
    <Grid2>
      <DR label="Name"  value={payment.user?.name} />
      <DR label="Email" value={payment.user?.email} />
      <DR label="Phone" value={payment.user?.phone} />
    </Grid2>

    <Sec>💳 Payment Info</Sec>
    <Grid2>
      <DR label="Amount" value={`₹${fmt(payment.amount_rupees)}`} />
      <DR label="Currency" value={payment.currency} />
      <DR label="Status" value={<Badge status={payment.status} />} />
      <DR label="Type" value={payment.payable_type} />
      <DR label="Created" value={fmtDT(payment.created_at)} />
    </Grid2>

    <Sec>🔎 Payable Details</Sec>
    <Grid2>
      {payment.payable_type === 'Booking' && (
        <>
          <DR label="Booking ID" value={`#${payment.payable_details?.booking_id}`} />
          <DR label="Car ID" value={`#${payment.payable_details?.car_id}`} />
          <DR label="Booking Status" value={payment.payable_details?.booking_status} />
        </>
      )}

      {payment.payable_type === 'CarInspection' && (
        <>
          <DR label="Inspection ID" value={`#${payment.payable_details?.inspection_id}`} />
          <DR label="Inspection Fee" value={`₹${fmt(payment.payable_details?.inspection_fee)}`} />
          <DR label="Inspection Status" value={payment.payable_details?.inspection_status} />
        </>
      )}
    </Grid2>

    <Sec>🏦 Razorpay Details</Sec>
    <Grid2>
      <DR label="Order ID" value={<code>{payment.razorpay_order_id}</code>} />
      <DR label="Payment ID" value={<code>{payment.razorpay_payment_id}</code>} />
    </Grid2>

    {payment.status === 'success' && (
      <button
        onClick={() => { onRefund(payment.id); onClose(); }}
        style={{
          marginTop:20,
          width:'100%',
          padding:12,
          background:'#7c3aed',
          color:'#fff',
          border:'none',
          borderRadius:8,
          fontWeight:600,
          cursor:'pointer'
        }}
      >
        ↩️ Refund Payment
      </button>
    )}

  </Modal>
);

// ── Booking Modal ─────────────────────────────────────────────────────────────
const BookingModal = ({ booking, onClose, onCancel, onConfirm }) => {
  const user = booking.user || {};
  const car  = booking.car  || {};
    // ---- Amount Calculations ----
  const carPrice       = Number(car.price || 0);
  const bookingAmount  = Number(booking.amount_cents || 0) / 100;
  const remainingAmount = carPrice - bookingAmount;

  return (
    <Modal onClose={onClose} title={`Booking #${booking.id}`}>
      <div style={{ marginBottom:12 }}><Badge status={booking.status} /></div>

      {/* Customer */}
      <Sec>👤 Customer Details</Sec>
      <Grid2>
        <DR label="Name"    value={user.name  || `User #${booking.user_id}`} />
        <DR label="Email"   value={user.email} />
        <DR label="Phone"   value={user.phone || user.mobile_number} />
        <DR label="User ID" value={`#${booking.user_id}`} />
      </Grid2>

      {/* Car */}
      <Sec>🚗 Car Details</Sec>
      <Grid2>
        <DR label="Title"        value={car.title || (car.brand ? `${car.brand} ${car.model}` : `Car #${booking.car_id}`)} />
        <DR label="Brand / Model" value={`${car.brand||'—'} / ${car.model||'—'}`} />
        <DR label="Year"         value={car.year} />
        <DR label="Color"        value={car.color} />
        <DR label="Fuel"         value={car.fuel_type} />
        <DR label="Transmission" value={car.transmission} />
        <DR label="Location"     value={car.location} />
        <DR label="Car ID"       value={`#${booking.car_id}`} />
      </Grid2>

      {/* Booking Amount */}
      <Sec>💰 Booking Amount Details</Sec>

      <div
        style={{
          background:'#fff7ed',
          border:'1px solid #fed7aa',
          borderRadius:12,
          padding:18,
          marginBottom:16
        }}
      >
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px 20px' }}>

          <DR
            label="Car Price (Total)"
            value={
              <span style={{ fontWeight:700, fontSize:15 }}>
                ₹{fmt(carPrice)}
              </span>
            }
          />

          <DR
            label="Booking Amount Paid"
            value={
              <span style={{ fontWeight:700, fontSize:15, color:'#16a34a' }}>
                ₹{fmt(bookingAmount)}
              </span>
            }
          />

          <DR
            label="Remaining Amount (To Be Paid)"
            value={
              <span style={{ fontWeight:700, fontSize:15, color:'#dc2626' }}>
                ₹{fmt(remainingAmount > 0 ? remainingAmount : 0)}
              </span>
            }
          />

          <DR
            label="Currency"
            value={booking.currency || 'INR'}
          />

          <DR
            label="Reservation Period"
            value="48 hours"
          />

        </div>

        <div
          style={{
            marginTop:14,
            padding:10,
            background:'#eff6ff',
            borderRadius:8,
            fontSize:12,
            color:'#1d4ed8'
          }}
        >
          ℹ️ Remaining amount will be collected at the time of final purchase.
        </div>
      </div>

      <Grid2>
        <DR label="Booking ID"  value={`#${booking.id}`} />
        <DR label="Scheduled"   value={fmtDT(booking.scheduled_at)} />
        <DR label="Created"     value={fmtDT(booking.created_at)} />
        {booking.cancelled_at        && <DR label="Cancelled At"   value={fmtDT(booking.cancelled_at)} />}
        {booking.cancellation_reason && <DR label="Cancel Reason"  value={booking.cancellation_reason} />}
      </Grid2>

      {['pending','confirmed'].includes(booking.status) && (
        <div style={{ display:'flex', gap:10, marginTop:22 }}>
          {booking.status === 'pending' && (
            <button onClick={() => { onConfirm(booking.id); onClose(); }}
              style={{ flex:1, padding:12, background:'#16a34a', color:'#fff', border:'none', borderRadius:8, fontWeight:600, cursor:'pointer' }}
            >✅ Confirm Booking</button>
          )}
          <button onClick={() => onCancel(booking.id)}
            style={{ flex:1, padding:12, background:'#dc2626', color:'#fff', border:'none', borderRadius:8, fontWeight:600, cursor:'pointer' }}
          >🚫 Cancel Booking</button>
        </div>
      )}
    </Modal>
  );
};

// ── Table CSS-in-JS constants ─────────────────────────────────────────────────
const tbl   = { width:'100%', borderCollapse:'collapse' };
const TH    = { padding:'11px 16px', textAlign:'left', fontWeight:600, fontSize:11, color:'#6b7280', textTransform:'uppercase', letterSpacing:.5, whiteSpace:'nowrap' };
const TD    = { padding:'13px 16px', fontSize:13, color:'#111827', verticalAlign:'middle' };
const noData = { textAlign:'center', padding:40, color:'#9ca3af', fontSize:14 };
const pill  = { background:'#eff6ff', color:'#1d4ed8', padding:'2px 8px', borderRadius:12, fontSize:11, fontWeight:600 };

export default PaymentsManagement;